// Drought Situation Overview report. Reads the time step + country from the URL
// and the `sharedData` handed over by the geoportal, loads the reporting
// statistics, and composes the report sections. The AI narrative is layered in
// via `useReportAiSummary` (Phase 2); a deterministic fallback keeps the page
// useful if the model is unavailable.

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";

import { TRACKING_ID } from "../Geoportal";
import { months } from "../sidepane/SidePanel";
import ColoredBar from "./ColoredBar";
import CustomChart from "./CustomChart";
import { buildMapRequest, INDICATOR_ROWS } from "./reportConfig";
import { normalizeBounds } from "./geo";
import { formatDekadDate, previousDekad, previousMonth } from "./dekad";
import { forecastPeriodFor } from "./forecast";
import { cleanNarrative } from "./narrative";
import useReportData from "./useReportData";
import useReportAiSummary from "./useReportAiSummary";
import {
  buildCdiExplanation,
  buildExecutiveSummary,
  INDICATOR_FALLBACK_NOTES,
} from "./fallbackNarrative";
import ReportControls from "./components/ReportControls";
import ExecutiveSummary from "./components/ExecutiveSummary";
import CdiOverviewPanel from "./components/CdiOverviewPanel";
import IndicatorRow from "./components/IndicatorRow";
import ReportMap from "./components/ReportMap";
import ImpactStatsTable from "./components/ImpactStatsTable";
import ReportActions from "./components/ReportActions";
import "../styles/ReportingPageStyle.css";

ReactGA.initialize(TRACKING_ID);

function useReportParams() {
  const location = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    const monthLabel = params.get("month") || "";
    return {
      year: params.get("year") || "",
      monthLabel,
      monthInt: months[monthLabel] || "",
      dekad: (params.get("dekad") || "01").padStart(2, "0"),
      countryCode: params.get("adminZeroCode") || "",
      // Present on shared links so a cold open still shows the country name.
      countryParam: params.get("country") || "",
    };
  }, [location.search]);
}

function readSharedCountry() {
  try {
    const raw = localStorage.getItem("sharedData");
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed?.admin_info?.admin_zero_info || {};
  } catch (e) {
    return {};
  }
}

export default function ReportingPage() {
  const { year, monthInt, dekad, countryCode, countryParam } = useReportParams();
  const navigate = useNavigate();
  const location = useLocation();
  const reportRef = useRef(null);

  const [scope, setScope] = useState("national");
  const [adminOne, setAdminOne] = useState(null);
  // A country picked from the National dropdown overrides the geoportal handoff.
  const [pickedCountry, setPickedCountry] = useState(null);

  const sharedCountry = useMemo(readSharedCountry, []);
  const handoffCountry = useMemo(
    () => ({
      code: sharedCountry.country_code || countryCode,
      name: sharedCountry.country_name || countryParam || "the selected country",
      bounds: normalizeBounds(sharedCountry.country_bounds),
    }),
    [sharedCountry, countryCode, countryParam],
  );
  const activeCountry = pickedCountry || handoffCountry;
  const countryId = activeCountry.code;
  const countryName = activeCountry.name;
  // Country bbox for the direct WMS map images (falls back to Africa-wide).
  const mapBounds = activeCountry.bounds || null;

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
      title: "reporting",
    });
  }, []);

  const { cdiOverview, cdiSummary, indicatorSeries, impactStats, productOverviews, loading } =
    useReportData({ countryId, year, monthInt, dekad });

  const ai = useReportAiSummary({
    countryId,
    countryName,
    year,
    monthInt,
    dekad,
    scope,
    adminOne: adminOne?.label || null,
    enabled: Boolean(countryId && year && monthInt && dekad),
  });

  const referenceDate = `${year}-${monthInt}-${dekad}`;

  // The two dekads before the reference dekad, for the CDI comparison row.
  const prevSteps = useMemo(() => {
    if (!year || !monthInt || !dekad) return [];
    const p1 = previousDekad({ year, monthInt, dekad });
    const p2 = previousDekad(p1);
    return [p1, p2];
  }, [year, monthInt, dekad]);

  // GPCC SPI feeding the CDI lags the analysis period by one calendar month.
  const spiGpccStep = useMemo(
    () => (year && monthInt ? previousMonth({ year, monthInt }) : null),
    [year, monthInt],
  );

  // Rolling 3-month season code for the ACMAD long-range forecast.
  const forecastPeriod = useMemo(() => forecastPeriodFor(monthInt), [monthInt]);

  const noteFor = (key) =>
    key
      ? cleanNarrative(ai.blocks[key]) || INDICATOR_FALLBACK_NOTES[key.replace(/_note$/, "")]
      : null;

  const mapCaption = (m) => {
    if (m.mapIndicator === "cdi") {
      const step = prevSteps[(m.step || 1) - 1];
      return step ? formatDekadDate(step) : `${year}-${monthInt}`;
    }
    if (m.mapIndicator === "spi") {
      return spiGpccStep ? formatDekadDate(spiGpccStep) : `${year}-${monthInt}`;
    }
    if (m.mapIndicator === "spi_era5") {
      return `${year}-${monthInt}, 1st dekad`;
    }
    if (m.mapIndicator === "longrange_forecast") {
      return forecastPeriod ? `${forecastPeriod} ${year} season` : `${year}`;
    }
    if (m.mapIndicator === "unwdc") {
      return `${year}-${monthInt}, 3-month`;
    }
    return formatDekadDate({ year, monthInt, dekad });
  };

  const indicatorRows = useMemo(() => {
    const ctx = { year, monthInt, dekad, prevSteps, spiGpccStep, forecastPeriod };
    return INDICATOR_ROWS.map((row) => ({
      id: row.id,
      title: row.title,
      maps: row.maps.map((m) => ({
        key: m.key,
        label: m.label,
        chart: m.chart,
        series: m.seriesParam === "cdi" ? cdiSummary : indicatorSeries[m.seriesParam],
        map: buildMapRequest(m, ctx),
        caption: mapCaption(m),
        note: noteFor(m.noteKey),
        detail: noteFor(m.detailNoteKey),
      })),
      sharedNotes: (row.sharedNoteKeys || []).map(noteFor).filter(Boolean),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, monthInt, dekad, prevSteps, spiGpccStep, forecastPeriod, cdiSummary, indicatorSeries, ai.blocks]);

  // AI text where available, otherwise the deterministic fallback. Hold the
  // fallback back until the CDI data has loaded so we never flash a
  // "no drought" message while the request is still in flight.
  const statsReady = Boolean(cdiOverview) || !loading;
  const execSummary =
    cleanNarrative(ai.blocks.executive_summary) ||
    (statsReady
      ? buildExecutiveSummary({
          cdiOverview,
          countryName,
          scope,
          adminOne,
          forecast: indicatorSeries.longrange_forecast,
        })
      : "");
  const cdiExplanation = cleanNarrative(ai.blocks.cdi_note) || buildCdiExplanation(cdiOverview);

  const meta = {
    periodCycle: "Dekadal",
    country: countryName,
    cdiDate: referenceDate,
    spiDate: `${year}-${monthInt}`,
  };

  const handleControlsChange = ({ scope: nextScope, country, adminOne: nextAdminOne }) => {
    if (nextScope !== undefined) setScope(nextScope);
    if (nextAdminOne !== undefined) setAdminOne(nextAdminOne);
    if (country !== undefined) {
      const resolved = country
        ? { code: country.code, name: country.name, bounds: normalizeBounds(country.bounds) }
        : null;
      setPickedCountry(resolved);
      setAdminOne(null);
      // Keep the URL in step so a refresh or a shared link resolves the same country.
      if (resolved) {
        const params = new URLSearchParams(location.search);
        params.set("adminZeroCode", String(resolved.code));
        params.set("country", resolved.name);
        navigate({ search: params.toString() }, { replace: true });
      }
    }
  };

  return (
    <div className="reporting-page">
      <nav className="report-breadcrumb hide-on-print">
        <span>Reports</span>
        <span aria-hidden="true">›</span>
        <span>{countryName}</span>
        <span aria-hidden="true">›</span>
        <span className="is-current">Drought overview</span>
      </nav>

      <div ref={reportRef} className="report-body">
        <header className="report-title-card">
          <h1>Drought Situation Overview Report for {countryName}</h1>
          <p>Reference date: {referenceDate}</p>
        </header>

        <ReportControls
          activeCountry={activeCountry}
          scope={scope}
          adminOne={adminOne}
          onChange={handleControlsChange}
        />

        <ExecutiveSummary
          summaryText={execSummary}
          streaming={ai.streaming}
          error={ai.error}
          meta={meta}
        />

        <section className="report-section">
          <ColoredBar
            productName="Combined Drought Index (CDI)"
            description={
              productOverviews.cdi ||
              "CDI combines precipitation, soil moisture, and vegetation condition to identify Watch, Warning, Alert, and recovery areas."
            }
          />
          <div className="cdi-section">
            <div className="cdi-section__map">
              <div className="cdi-section__canvas">
                <ReportMap
                  req={{
                    indicator: "cdi",
                    year: Number(year),
                    month: Number(monthInt),
                    dekad: Number(dekad),
                  }}
                  bounds={mapBounds}
                  countryId={countryId}
                  alt={`CDI map for ${countryName}, ${referenceDate}`}
                />
              </div>
              <p className="report-map-caption">
                Map showing drought situation for the period: {referenceDate}
              </p>

              <div className="cdi-timeseries">
                <p className="report-subhead">Ten-day CDI time series</p>
                {Array.isArray(cdiSummary) && cdiSummary.length > 0 ? (
                  <CustomChart data={cdiSummary} />
                ) : (
                  <p className="report-empty">
                    {loading ? "Loading CDI time series…" : "No CDI time series for this period."}
                  </p>
                )}
              </div>
            </div>
            <CdiOverviewPanel
              cdiOverview={cdiOverview}
              explanation={cdiExplanation}
              loading={loading}
            />
          </div>
        </section>

        <section className="report-section">
          <ColoredBar
            productName="Indicator maps and supporting analysis"
            description="Supporting indicators, paired for comparison: each row sets two related maps side by side with a short read of what they signal together for water, crops and rangelands."
          />
          <div className="indicator-rows">
            {indicatorRows.map((row) => (
              <IndicatorRow
                key={row.id}
                title={row.title}
                maps={row.maps}
                sharedNotes={row.sharedNotes}
                countryId={countryId}
                bounds={mapBounds}
              />
            ))}
          </div>
        </section>

        <ImpactStatsTable
          stats={impactStats?.available ? impactStats.phases : null}
          reason={impactStats?.reason}
        />
      </div>

      <ReportActions
        getElement={() => reportRef.current}
        fileName={`drought-report-${countryName}-${referenceDate}`.replace(/\s+/g, "-")}
        shareTitle={`Drought Situation Overview Report for ${countryName}, ${referenceDate}`}
        shareParams={{ country: countryName }}
      />
    </div>
  );
}
